/* eslint-disable jsx-a11y/alt-text */
import { IonIcon, IonRow, IonText } from '@ionic/react';
import HStack from 'components/HStack';
import {
  star,
  starOutline,
  calendarOutline,
  locationOutline,
  flagOutline,
  trophy,
  medal,
  personCircleOutline,
} from 'ionicons/icons';
import { observer } from 'mobx-react-lite';
import { useHistory } from 'react-router';
import './ExploreFavoriteCard.css';

import { ExploreElement } from 'models/ExploreElementModel/ExploreElementModel';
import { isMockElement } from '../../../util/mockDataDetection';

// GENERISCHES Interface für bessere Kompatibilität
interface ExploreCardItem {
  id: string;
  name: string;
  type: string | any; // Flexibel für MobX und Enum Types
  startDate: Date;
  endDate: Date; // Optional, falls nicht für alle Typen relevant
  favorite: boolean;
  location: string;
  nationality: string;
  gender?: string;
  age?: number;
}

interface ExploreFavoriteCardProps {
  item: ExploreCardItem;
  isFriend: boolean;
  onOpenPersonModal?: (item: ExploreElement) => void;
  onOpenEventModal?: (item: ExploreElement) => void;
  onOpenRankingModal?: (item: ExploreElement) => void;
  onOpenContestModal?: (item: ExploreElement) => void;
}

const ExploreFavoriteCard: React.FC<ExploreFavoriteCardProps> = observer(
  ({
    item,
    onOpenPersonModal,
    onOpenEventModal,
    onOpenRankingModal,
    onOpenContestModal,
  }) => {
    const history = useHistory();

    const navigateToDetailPage = () => {
      const typeDisplayName = getTypeDisplayName(item.type).toLowerCase();

      // Check the type and call the appropriate modal handler
      if (typeDisplayName === 'person' && onOpenPersonModal) {
        onOpenPersonModal(item as any);
      } else if (typeDisplayName === 'event' && onOpenEventModal) {
        onOpenEventModal(item as any);
      } else if (typeDisplayName === 'ranking' && onOpenRankingModal) {
        onOpenRankingModal(item as any);
      } else if (typeDisplayName === 'contest' && onOpenContestModal) {
        onOpenContestModal(item as any);
      } else {
        // Default navigation for other types
        history.push(`/trick/${item.id}`);
      }
    };

    const handleStarClick = (ev: React.MouseEvent) => {
      ev.preventDefault();
      ev.stopPropagation();

      // Toggle favorite status
      (item as ExploreElement).toggleFavorite();

      return false;
    };

    // Helper function für Type Display
    const getTypeDisplayName = (type: string | any): string => {
      if (typeof type === 'string') {
        return type;
      }
      return String(type);
    };

    // Generiere eine zufällige Punktzahl für die Person (500-1000)
    const generateScore = () => {
      // Verwende eine consistent score basierend auf der ID
      const idSum = item.id
        .split('')
        .reduce((sum, char) => sum + char.charCodeAt(0), 0);
      return 500 + (idSum % 501); // Score zwischen 500 und 1000
    };

    const getPositionIcon = (position: number) => {
      switch (position) {
        case 1:
          return <IonIcon icon={trophy} className="position-icon gold" />;
        case 2:
          return <IonIcon icon={medal} className="position-icon silver" />;
        case 3:
          return <IonIcon icon={medal} className="position-icon bronze" />;
        default:
          return <span className="position-number">{position}</span>;
      }
    };

    const renderTypeSpecificContent = () => {
      const itemType = getTypeDisplayName(item.type).toLowerCase();

      if (itemType === 'event') {
        return (
          <div className="meta-container">
            <HStack className="meta-date-row">
              <IonIcon icon={calendarOutline} className="meta-date-icon" />
              <IonText className="meta-date-text">
                {item.startDate.toLocaleDateString('de-DE', {
                  day: '2-digit',
                  month: '2-digit',
                  year: '2-digit',
                })}
              </IonText>
            </HStack>
            {item.location && (
              <HStack className="meta-row">
                <HStack className="meta-item">
                  <IonIcon icon={locationOutline} className="meta-icon" />
                  <IonText className="meta-text">{item.location}</IonText>
                </HStack>
              </HStack>
            )}
          </div>
        );
      } else if (itemType === 'ranking') {
        // Generiere Mock-Daten für die ersten drei Plätze
        const mockTopThree = [
          { position: 1, name: 'Max M.', score: 2450 },
          { position: 2, name: 'Anna S.', score: 2380 },
          { position: 3, name: 'Tom W.', score: 2290 },
        ];

        return (
          <div className="meta-container">
            <div className="ranking-preview-compact">
              {mockTopThree.slice(0, 2).map((entry) => (
                <div
                  key={entry.position}
                  className="ranking-preview-item-compact"
                >
                  <div className="ranking-position-compact">
                    {getPositionIcon(entry.position)}
                  </div>
                  <div className="ranking-name-compact">{entry.name}</div>
                  <div className="ranking-score-compact">{entry.score}</div>
                </div>
              ))}
            </div>
          </div>
        );
      } else if (itemType === 'person') {
        const personScore = generateScore();

        return (
          <div className="meta-container">
            <div className="person-info-compact">
              {/* Score Row */}
              <div className="meta-row">
                <div className="meta-item">
                  <IonIcon icon={personCircleOutline} className="meta-icon" />
                  <IonText className="meta-text">Score: {personScore}</IonText>
                </div>
              </div>
              {/* Location/Nationality Row */}
              <div className="meta-row">
                {(item as any).location && (
                  <div className="meta-item">
                    <IonIcon icon={locationOutline} className="meta-icon" />
                    <IonText className="meta-text">
                      {(item as any).location}
                    </IonText>
                  </div>
                )}
                {(item as any).nationality && (
                  <div className="meta-item">
                    <IonIcon icon={flagOutline} className="meta-icon" />
                    <IonText className="meta-text">
                      {(item as any).nationality}
                    </IonText>
                  </div>
                )}
                {/* Fallback: Show country if location/nationality are not available */}
                {!((item as any).location || (item as any).nationality) &&
                  item.location && (
                    <div className="meta-item">
                      <IonIcon icon={locationOutline} className="meta-icon" />
                      <IonText className="meta-text">{item.location}</IonText>
                    </div>
                  )}
              </div>
            </div>
          </div>
        );
      } else if (itemType === 'contest') {
        // Contest-spezifische Daten
        const startDate = item.startDate;
        const endDate = item.endDate;

        // Contest Status basierend auf Datum
        const now = new Date();
        let contestStatus = 'upcoming';
        let statusText = 'Bevorstehend';

        if (startDate && endDate) {
          if (now >= startDate && now <= endDate) {
            contestStatus = 'active';
            statusText = 'Aktiv';
          } else if (now > endDate) {
            contestStatus = 'finished';
            statusText = 'Beendet';
          }
        }

        return (
          <div className="meta-container">
            <div className={`contest-status-badge-compact ${contestStatus}`}>
              {statusText}
            </div>
            <HStack className="meta-date-row">
              <IonIcon icon={calendarOutline} className="meta-date-icon" />
              <IonText className="meta-date-text">
                {startDate.toLocaleDateString('de-DE', {
                  day: '2-digit',
                  month: '2-digit',
                })}
                {' - '}
                {endDate.toLocaleDateString('de-DE', {
                  day: '2-digit',
                  month: '2-digit',
                })}
              </IonText>
            </HStack>
            {(item as any).location && (
              <HStack className="meta-row">
                <HStack className="meta-item">
                  <IonIcon icon={locationOutline} className="meta-icon" />
                  <IonText className="meta-text">
                    {(item as any).location}
                  </IonText>
                </HStack>
              </HStack>
            )}
          </div>
        );
      }

      // Fallback für andere Typen
      return (
        <div className="meta-container">
          {item.location && (
            <HStack className="meta-row">
              <HStack className="meta-item">
                <IonIcon icon={locationOutline} className="meta-icon" />
                <IonText className="meta-text">{item.location}</IonText>
              </HStack>
            </HStack>
          )}
        </div>
      );
    };

    return (
      <div className="favorite-card" onClick={navigateToDetailPage}>
        <div className="card-header">
          {/* Star Icon for favorite toggle */}
          <IonIcon
            icon={item.favorite ? star : starOutline}
            className="favorite-star-icon"
            onClick={handleStarClick}
          />
        </div>
        <div className="card-body">
          <div className="card-content">
            {/* Header nur mit Name und Mock Pill links */}
            <IonRow className="ion-justify-content-between ion-align-items-start">
              <HStack className="card-header-row">
                {/* Mock Pill links vom Namen */}
                {isMockElement(item as ExploreElement) && (
                  <div className="mock-pill">PROXY</div>
                )}

                {/* Name */}
                <IonText className="card-name">{item.name}</IonText>
              </HStack>
            </IonRow>

            {/* Meta Information - Type-specific content */}
            {renderTypeSpecificContent()}

            {/* Spacer */}
            <div className="card-spacer" />
          </div>
        </div>
      </div>
    );
  }
);

export default ExploreFavoriteCard;
